import React, { useState, useEffect } from "react";
import useCustomMutation from "../../../../Hooks/useCustomMutation";
import { Row, Col, Form, FormGroup, Label, Input, Alert } from "reactstrap";
import SwitchButton from "../../../Buttons/SwitchButton";
import formatDate from "../../../../utils/formatDate";
import ButtonSubmit from "../../../Buttons/ButtonSubmit";

const EditTicket = ({
  ticketDetails,
  token,
  eventDetails,
  setIsOffcanvasOpen,
}) => {
  const [inputs, setInputs] = useState({
    tier: "",
    price: "",
    quantity: "",
    discount: "",
    allowEarlyBird: "false",
    earlyBirdStartDate: "",
    earlyBirdEndDate: "",
    closingDate: "",
    ticketDescription: "",
    ticketTerms: "",
  });

  console.log("...inputs", JSON.stringify(inputs, null, 2));
  const [validationErrors, setValidationErrors] = useState([]);

  const key = ["EventDetails", eventDetails?._id];
  const mutation = useCustomMutation(key);

  const validateDates = () => {
    const errors = [];
    const { earlyBirdStartDate, earlyBirdEndDate, closingDate } = inputs;

    if (inputs?.allowEarlyBird) {
      if (new Date(earlyBirdStartDate) >= new Date(earlyBirdEndDate)) {
        errors.push("Early bird start date must be before the end date.");
      }
      if (new Date(earlyBirdStartDate) >= new Date(closingDate)) {
        errors.push("Early bird start date must be before the closing date.");
      }
      if (new Date(earlyBirdEndDate) > new Date(closingDate)) {
        errors.push("Early bird end date must not be after the closing date.");
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const toggleIsEarlyBird = () => {
    setInputs({ ...inputs, allowEarlyBird: !inputs?.allowEarlyBird });
  };

  const handleSubmitForm = (event) => {
    event.preventDefault();

    if (!validateDates()) {
      return; // Don't submit if validation fails
    }

    const url = `ticket-settings/${ticketDetails?._id}`;
    const raw = JSON.stringify({
      tier: inputs.tier,
      price: parseInt(inputs.price, 10),
      quantity: parseInt(inputs.quantity, 10),
      discount: inputs?.allowEarlyBird ? parseInt(inputs.discount, 10) : null,
      allowEarlyBird: inputs?.allowEarlyBird,
      earlyBirdStartDate: inputs?.allowEarlyBird
        ? formatDate(inputs.earlyBirdStartDate)
        : "",
      earlyBirdEndDate: inputs?.allowEarlyBird
        ? formatDate(inputs.earlyBirdEndDate)
        : "",
      closingDate: formatDate(inputs.closingDate),
      ticketDescription: inputs.ticketDescription,
      ticketTerms: inputs.ticketTerms,
    });

    const data = {
      endPoint: url,
      bodyContent: raw,
      Method: "PATCH",
      displayMessage: "Ticket Successfully Updated",
      token: token,
      isJSON: null,
    };
    mutation.mutate(data);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prevInputs) => ({
      ...prevInputs,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (ticketDetails) {
      const parseDate = (date) => {
        const parsedDate = new Date(date);
        return isNaN(parsedDate)
          ? ""
          : parsedDate.toISOString().substring(0, 10);
      };

      setInputs({
        tier: ticketDetails.tier || "",
        price: ticketDetails.price || "",
        quantity: ticketDetails.quantity || "",
        discount: ticketDetails.discount || "",
        allowEarlyBird: ticketDetails?.allowEarlyBird || false,
        earlyBirdStartDate: parseDate(ticketDetails.earlyBirdStartDate),
        earlyBirdEndDate: parseDate(ticketDetails.earlyBirdEndDate),
        closingDate: parseDate(ticketDetails.closingDate),
        ticketDescription: ticketDetails.ticketDescription || "",
        ticketTerms: ticketDetails.ticketTerms || "",
      });
    }
  }, [ticketDetails]);

  useEffect(() => {
    if (mutation?.isSuccess) {
      setIsOffcanvasOpen(false);
    }
  }, [mutation?.isSuccess, setIsOffcanvasOpen]);

  useEffect(() => {
    if (inputs?.allowEarlyBird) {
      validateDates();
    } else {
      setValidationErrors([]);
    }
  }, [
    inputs.earlyBirdStartDate,
    inputs.earlyBirdEndDate,
    inputs.closingDate,
    inputs?.allowEarlyBird,
  ]);

  const renderInput = (label, name, type = "text", required = false) => (
    <FormGroup>
      <Label for={name}>{label}</Label>
      <Input
        type={type}
        name={name}
        id={name}
        value={inputs[name]}
        onChange={handleChange}
        required={required}
      />
    </FormGroup>
  );

  return (
    <Form onSubmit={handleSubmitForm}>
      <Row>
        <Col md={6}>{renderInput("Tier", "tier")}</Col>
        <Col md={6}>{renderInput("Price", "price", "number")}</Col>
        <Col md={6}>{renderInput("Quantity", "quantity", "number")}</Col>
        <Col md={6}>{renderInput("Discount", "discount", "number")}</Col>
        <Col md={12}>
          <div className="d-flex mt-2 mb-2">
            <SwitchButton
              isOn={inputs?.allowEarlyBird}
              handleToggle={() => toggleIsEarlyBird()}
            />

            <div className="mx-2">
              <p style={{ color: "#f68a20", fontWeight: "500" }}>
                Include Early Bird Price
              </p>
            </div>
          </div>
        </Col>
        {validationErrors.length > 0 && (
          <Alert color="warning" className="mt-3">
            <ul>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Row className="mt-3">
          {inputs?.allowEarlyBird && (
            <>
              <Col md={4}>
                {renderInput(
                  "Early Bird Start Date",
                  "earlyBirdStartDate",
                  "date"
                )}
              </Col>
              <Col md={4}>
                {renderInput("Early Bird End Date", "earlyBirdEndDate", "date")}
              </Col>
            </>
          )}
          <Col md={4}>
            {renderInput("Closing Date", "closingDate", "date", true)}
          </Col>
        </Row>

        {renderInput("Ticket description", "ticketDescription", "textarea")}

        {renderInput("Ticket Terms", "ticketTerms", "textarea")}

        <div className="w-100 d-flex justify-content-center mt-3">
          <ButtonSubmit
            Title="Update Ticket"
            BackgroundColor="#f68a20"
            ColorText="#fff"
            BorderColor="#f68a20"
            borderRadius={25}
            handleOnclick={handleSubmitForm}
            pending={mutation.isLoading}
            disabled={validationErrors.length > 0}
          />
        </div>
      </Row>
    </Form>
  );
};

export default EditTicket;
